/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSignature } from "../security/signature";
import { API_BASE_URL } from "./config";
import { decrypt, encrypt } from "../security/encryption";
import { triggerLogout } from "../auth/authEvents";
import { getAccessToken, setAccessToken } from "./authTokens";
import { performTokenRefresh } from "./auth";
import { set } from "mongoose";


type HTTPMethod = "GET" | "POST" | "PUT" | "DELETE";
type BodyType = "json" | "formData"
type ResponseType = "json" | "blob";


interface FetchOptions {
  method?: HTTPMethod;
  data?: Record<string, any>;
  type?: BodyType;
  responseType?: ResponseType;
  headers?: HeadersInit;
  timeoutMs?: number;
}



export const apiFetch = async (
  url: string,
  {
    method = "GET",
    data,
    type = "json",
    responseType = "json",
    headers = {},
    timeoutMs = 120000,
  }: FetchOptions = {}
): Promise<any> => {
  // let accessToken = localStorage.getItem("token");
  let accessToken = await getAccessToken();
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  const config: RequestInit = {
    method,
    credentials: "include", // ✅ send cookies (refresh token)
    signal: controller.signal,
    headers: {
      ...headers,
    },
  };

  /* ---------------- Authorization ---------------- */
  if (accessToken) {
    (config.headers as Record<string, string>)["Authorization"] =
      `Bearer ${accessToken}`;
  }

  /* ---------------- Security Signature ---------------- */
  if (url.includes("/change/password")) {
    const ts = Date.now();
    const signature = await createSignature(ts, method, data ?? {});

    (config.headers as Record<string, string>)["x-signature"] = signature;
    (config.headers as Record<string, string>)["x-timestamp"] = ts.toString();
  }

  /* ---------------- Body Handling ---------------- */
  if (data) {
    if (type === "formData") {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value);
      });
      config.body = formData;
    } else {
      config.body = JSON.stringify(data);
      (config.headers as Record<string, string>)["Content-Type"] =
        "application/json";
    }
  }

  /* ---------------- Fetch + Refresh Logic ---------------- */
  try {

    let response = await fetch(`${url}`, config);

    clearTimeout(timeout);

    // 🔁 Access token expired
    if (response.status === 401) {
      const newAccessToken = await performTokenRefresh();

      if (!newAccessToken) {
        throw new Error("Session expired");
      }

      // Retry original request with new token
      (config.headers as Record<string, string>)["Authorization"] =
        `Bearer ${newAccessToken}`;

      response = await fetch(`${url}`, config);
      if (!response.ok) {
        throw new Error("Request failed after token refresh");
      }
    }

    if (!response.ok) {
      let errorData: any;
      try {
        errorData = await response.json();
      } catch {
        errorData = await response.text();
      }
      return Promise.reject(errorData);
    }

    // ✅ Handle file downloads
    if (responseType === "blob") {
      return await response.blob();
    }

    return response.json();
  } catch (error: any) {
    if (error.name === "AbortError") {
      throw new Error("Request timed out");
    }
    throw error;
  }
};
